import { Dialog, DialogTitle } from '@mui/material'
import { useCallback, useState } from 'react'

import useAccessTokenAndShopId from '../../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import {
  PartClaimed,
  PartInCart
} from '../../../hooks/useTransformServiceItems/useTransformServiceItems'
import {
  useUnlinkInventoryMutation,
  useUpdateQuantityMutation
} from '../../../redux/api'
import {
  InventoryPartInstanceEntry,
  PartOverrideUnitOfMeasureEnum
} from '../../../sdk'
import { useSubmitLinkAndClaim } from '../useLookupPartsDialog/useSubmitLinkAndClaim'
import { ClaimInventoryProps, ClaimPartForm } from './ClaimPartForm'
export function getDefaultQuantity(available: number, request: number): number {
  return Math.min(available, request)
}
export const useClaimPartDialog = () => {
  const { accessToken, shopId } = useAccessTokenAndShopId()
  const [customProps, setCustomProps] = useState<
    ClaimInventoryProps | undefined
  >()

  const [unlinkInventory] = useUnlinkInventoryMutation()
  const [updateQuantity] = useUpdateQuantityMutation()
  const {
    submitLinkAndClaimForExPart,
    submitCreateCores,
    submitLinkAndClaimForNewPart
  } = useSubmitLinkAndClaim()
  const handleClaimLinkedParts = useCallback(
    async (
      instancesClaimed: InventoryPartInstanceEntry[],
      part: PartClaimed
    ) => {
      const unClaimedLinkIds = Object.keys(part.linkedInstances).filter(
        (linkId) => !(linkId in part.claimedInstances)
      )
      let linkIdsToRemove: string[] = []
      if (part.unitOfMeasure === PartOverrideUnitOfMeasureEnum.DiscreteUnit) {
        // for non-fluids, unlink numbers of new claimed instance
        linkIdsToRemove = unClaimedLinkIds.slice(0, instancesClaimed.length)
      } else {
        // for fluids, unlink all other parts, part-override represents the quantity
        linkIdsToRemove = unClaimedLinkIds
      }
      if (linkIdsToRemove.length > 0) {
        await unlinkInventory({
          authorization: accessToken,
          shop: shopId,
          linkId: linkIdsToRemove
        })
      }
    },
    [accessToken, shopId, unlinkInventory]
  )
  const handleClaimPartsInCart = useCallback(
    async (
      instancesClaimed: InventoryPartInstanceEntry[],
      part: PartInCart
    ) => {
      await updateQuantity({
        authorization: accessToken,
        updateQuantityRequest: {
          service: part.overrideEntry.service,
          partUuid: part.id,
          quantity: part.quantity - instancesClaimed.length
        }
      })
    },
    [accessToken, updateQuantity]
  )

  const handleClaim = useCallback(
    async (customProps: ClaimInventoryProps, hasCore: boolean) => {
      const { part, type } = customProps.partAndType
      const requiredQuantity = type === 'claimed' ? part.need : part.quantity
      let instancesToClaimFinal: InventoryPartInstanceEntry[] = []
      if (type === 'claimed') {
        const { instancesToClaim } = await submitLinkAndClaimForExPart(
          customProps.inventoryPart,
          part.unitOfMeasure,
          requiredQuantity,
          part.overrideEntry
        )
        instancesToClaimFinal = instancesToClaim
        await handleClaimLinkedParts(instancesToClaim, part)
      }
      if (type === 'inCart') {
        const { instancesToClaim } = await submitLinkAndClaimForNewPart(
          customProps.inventoryPart,
          part.unitOfMeasure,
          requiredQuantity,
          part.overrideEntry.service!,
          part.overrideEntry.markup,
          part.overrideEntry.taxExempt,
          part.overrideEntry.oilFilter,
          part.overrideEntry.fluids
        )
        instancesToClaimFinal = instancesToClaim
        await handleClaimPartsInCart(instancesToClaim, part)
      }
      // if it's standsalone placehodler, no need to do anythin else
      if (hasCore) {
        await submitCreateCores(instancesToClaimFinal)
      }
      setCustomProps(undefined)
    },
    [
      handleClaimLinkedParts,
      handleClaimPartsInCart,
      submitCreateCores,
      submitLinkAndClaimForExPart,
      submitLinkAndClaimForNewPart
    ]
  )

  const ClaimPartDialog = useCallback(() => {
    return (
      <>
        {customProps && (
          <Dialog open={true} fullWidth={true} maxWidth={'xs'}>
            <DialogTitle data-testid="dialogTitle">
              Claim Items from Inventory
            </DialogTitle>
            <ClaimPartForm
              customProps={customProps}
              setCustomProps={setCustomProps}
              handleClaim={handleClaim}
            />
          </Dialog>
        )}
      </>
    )
  }, [customProps, handleClaim])

  return { setCustomProps, ClaimPartDialog }
}
